import React from 'react';
import { Input, InputProps as AntdInputProps, InputRef } from 'antd';
import { tv, type VariantProps } from 'tailwind-variants';
import { CloseOutlined } from '@ant-design/icons';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { iconEye } from 'utils/importFile';
import TextArea, { TextAreaProps } from 'antd/es/input/TextArea';
import { formatMoney } from 'utils';

const input = tv({
  base: 'outline-none transition-all',
  variants: {
    variantStatus: {
      default: 'border-[1px] border-[var(--thirdColor)]',
      error: 'border-red-500 focus:border-red-500 focus:shadow',
      success: 'border-green-500 focus:border-green-500 focus:shadow',
      underline: 'border-none focus:shadow-none',
      generate: 'border-none ',
    },
    radius: {
      none: 'rounded-none',
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      full: 'rounded-full',
      square: '',
      rounded: 'rounded-[8px]',
      circle: 'rounded-full',
    },
    hardSize: {
      xxs: 'h-[22px] text-[12px] px-[6px] py-[6px] leading-[18px]',
      xs: 'h-[24px] text-[14px] px-[6px] py-[8px] leading-[20px]',
      sm: 'h-[32px] text-[14px] px-[8px] py-[10px] leading-[20px]',
      md: 'h-[30px] xl:h-[36px] 2xl:h-[40px] text-[16px] leading-[24px] ',
      md2: 'h-[38px] text-[14px] leading-[20px]',
      lg: 'h-[48px] text-[16px] px-[16px] py-[12px] leading-[28px]',
      xl: 'h-[56px] text-[16px] px-[16px] py-[10px] leading-[32px]',
    },
    twSize: {
      xxs: '2xl:text-[12px] 2xl:px-[6px] 2xl:py-[6px] 2xl:h-[22px] 2xl:leading-[18px] xl:text-[11px] xl:px-[5px] xl:py-[5px] xl:h-[20px] xl:leading-[16px] lg:text-[10px] lg:px-[4px] lg:py-[4px] lg:h-[18px] lg:leading-[14px]',
      xs: '2xl:text-[14px] 2xl:px-[12px] 2xl:py-[8px] 2xl:h-[24px] 2xl:leading-[20px] xl:text-[14px] xl:px-[12px] xl:py-[8px] xl:h-[24px] xl:leading-[22px] lg:text-[14px] lg:px-[8px] lg:py-[6px] lg:h-[20px] lg:leading-[14px]',
      sm: '2xl:text-[14px] 2xl:px-[8px] 2xl:py-[10px] 2xl:h-[32px] xl:text-[11px] xl:px-[6px] xl:py-[8px] xl:h-[26px] lg:text-[9px] lg:px-[5px] lg:py-[6px] lg:h-[21px] 2xl:leading-[20px] xl:leading-[16px] lg:leading-[12px]',
      sm2: '2xl:text-[14px] 2xl:px-[9px] 2xl:py-[12px] 2xl:h-[38px] xl:text-[11px] xl:px-[6px] xl:py-[8px] xl:h-[26px] lg:text-[9px] lg:px-[5px] lg:py-[6px] lg:h-[21px] 2xl:leading-[20px] xl:leading-[16px] lg:leading-[12px]',
      md: 'min-h-[40px] 2xl:text-[16px] 2xl:px-[16px] 2xl:py-[8px] 2xl:h-[40px] xl:text-[14px] xl:px-[14px] xl:py-[6px] xl:h-[40px] lg:text-[12px] lg:px-[10px] lg:py-[5px] lg:h-[40px] 2xl:leading-[24px] xl:leading-[20px] lg:leading-[14px]',
      lg: '2xl:text-[16px] 2xl:px-[16px] 2xl:py-[12px] 2xl:h-[48px] xl:text-[13px] xl:px-[13px] xl:py-[10px] xl:h-[38px] lg:text-[10px] lg:px-[10px] lg:py-[8px] lg:h-[30px] 2xl:leading-[28px] xl:leading-[22px] lg:leading-[18px]',
      xl: '2xl:text-[16px] 2xl:px-[16px] 2xl:py-[10px] 2xl:h-[56px] xl:text-[13px] xl:px-[13px] xl:py-[8px] xl:h-[45px] lg:text-[10px] lg:px-[10px] lg:py-[6px] lg:h-[36px] 2xl:leading-[32px] xl:leading-[26px] lg:leading-[22px]',
    },

    fullWidth: {
      true: 'w-full',
      false: '',
    },
  },
  defaultVariants: {
    status: 'default',
    radius: 'md',
    fullWidth: true,
    hardSize: 'md',
  },
});

type InputCommonProps = AntdInputProps &
  VariantProps<typeof input> & {
    className?: string;
    ref?: React.Ref<InputRef>;
    addonAfterNode?: React.ReactNode;
    precision?: number;
  };

export const InputCommon: React.FC<InputCommonProps> = ({
  variantStatus,
  radius,
  fullWidth,
  twSize,
  className = '',
  ref,
  type = 'text',
  addonAfterNode,
  precision = 2,
  ...rest
}) => {
  const inputClass = `${input({ variantStatus, radius, fullWidth, twSize, ...rest })} ${className}`;

  const hasValue = rest.value || rest.defaultValue;

  return type === 'password' ? (
    <Input.Password
      ref={ref}
      className={inputClass}
      {...rest}
      iconRender={(visible) =>
        visible ? (
          <VisibilityOffIcon style={{ color: '#1E1E1E', fontSize: 15 }} />
        ) : (
          <img src={iconEye} alt='Ẩn mật khẩu' style={{ width: 15, height: 16, cursor: 'pointer' }} />
        )
      }
      allowClear={{ clearIcon: <CloseOutlined style={{ fontSize: 13, color: '#1E1E1E' }} /> }}
    />
  ) : type === 'label' ? (
    <div className='relative w-full'>
      {!hasValue && (
        <span className='absolute top-0 text-[var(--textBlack16)] z-1 h-6 w-full text-[15px] 2xl:text-xl leading-6 left-0 pointer-events-none font-bold'>
          {rest.placeholder}
        </span>
      )}
      <p className='absolute z-1 top-6 left-0 right-0 bg-[var(--textBlack12)] h-[1px] w-full pointer-events-none'></p>
      <Input
        {...rest}
        ref={ref}
        className={`${inputClass} !p-0 !pb-4 text-[15px] 2xl:text-xl caret-black !bg-transparent rounded-none !border-none !shadow-none `}
        placeholder=' '
      />
    </div>
  ) : type === 'textarea' ? (
    <TextArea ref={ref} className={`${inputClass}`} {...(rest as TextAreaProps)} />
  ) : type === 'money' ? (
    <Input
      {...rest}
      ref={ref}
      inputMode='numeric'
      className={inputClass}
      value={rest.value === 0 || rest.value === '' ? '0' : formatMoney(String(Number(rest.value)))}
      onChange={(e) => {
        let rawValue = e.target.value.replace(/[^\d]/g, '').slice(0, 15);
        const numericValue = Number(rawValue);

        // Kiểm tra min nếu có
        if (rest.min !== undefined && !isNaN(numericValue) && numericValue < Number(rest.min)) {
          rawValue = rest.min.toString();
        }
        // Kiểm tra max nếu có
        if (rest.max !== undefined && !isNaN(numericValue) && numericValue > Number(rest.max)) {
          rawValue = rest.max.toString();
        }

        const eventWithFormatted = {
          ...e,
          target: {
            ...e.target,
            value: rawValue,
          },
        };
        rest.onChange?.(eventWithFormatted as React.ChangeEvent<HTMLInputElement>);
      }}
    />
  ) : type === 'number' ? (
    <Input
      {...rest}
      ref={ref}
      type='number'
      className={inputClass}
      onChange={(e) => {
        let value = parseFloat(e.target.value);
        if (rest.min !== undefined && !isNaN(value) && value < (rest.min as number)) {
          value = rest.min as number;
        }

        if (rest.max !== undefined && !isNaN(value) && value > (rest.max as number)) {
          value = rest.max as number;
        }

        const eventWithValue = {
          ...e,
          target: {
            ...e.target,
            value: isNaN(value) ? '' : value.toString(),
          },
        };

        rest.onChange?.(eventWithValue as React.ChangeEvent<HTMLInputElement>);
      }}
    />
  ) : type === 'decimal' ? (
    <Input
      {...rest}
      ref={ref}
      type='text'
      inputMode='decimal'
      className={inputClass}
      onChange={(e) => {
        const { value } = e.target;

        // Cho phép chỉ số và tối đa một dấu chấm
        const regex = new RegExp(`^-?\\d*(\\.\\d{0,${precision}})?$`);

        if (value === '' || regex.test(value)) {
          let parsedValue = value === '' ? '' : value;

          // Kiểm tra min/max nếu có giá trị hợp lệ
          if (value !== '' && value !== '-' && !value.endsWith('.')) {
            const numValue = parseFloat(value);

            if (rest.min !== undefined && !isNaN(numValue) && numValue < (rest.min as number)) {
              parsedValue = (rest.min as number).toString();
            }

            if (rest.max !== undefined && !isNaN(numValue) && numValue > (rest.max as number)) {
              parsedValue = (rest.max as number).toString();
            }
          }

          const eventWithValue = {
            ...e,
            target: {
              ...e.target,
              value: parsedValue,
            },
          };

          rest.onChange?.(eventWithValue as React.ChangeEvent<HTMLInputElement>);
        }
      }}
    />
  ) : (
    <div className='relative w-full'>
      <Input ref={ref} className={`${inputClass}`} type={type} {...rest} />
      {addonAfterNode && (
        <div className='absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer'>{addonAfterNode}</div>
      )}
    </div>
  );
};
